import { FieldErrorsImpl, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import ErrorBadge from '../ErrorBadge/ErrorBadge';
import { ValuesType } from 'state/forms/forms.reducer';
import { FIELD_MISSING, VALUE_TOO_SHORT } from 'constants/validationMessages';
import FormField from '../../containers/FormField/FormField';
import { useAppSelector } from 'hooks/useRedux';
import { formsActions, formsSelectors } from 'state/forms';
import { useAppDispatch } from 'state/store';
import './RadioButton.scss';

interface singleRadioProps {
  item: string;
  value?: string | number;
  register: UseFormRegister<ValuesType>;
  formKey: string;
  required?: boolean;
  identifierForOther?: string;
  showIndicationInput?: boolean;
  isIndicationInputEnabled?: boolean;
  setValue?: UseFormSetValue<ValuesType>;
  registerForm?: boolean;
  isDisabled?: boolean;
}

export interface radioButtonProps {
  form: {
    id: number;
    options: string[];
    title: (required: boolean | undefined) => JSX.Element;
  };
  required?: boolean;
  register: UseFormRegister<ValuesType>;
  errors?: Partial<
    FieldErrorsImpl<{
      [x: string]: NonNullable<string | string[]>;
    }>
  >;
  formKey: string;
  identifierForOther?: string;
  showIndicationInput?: boolean;
  isIndicationInputEnabled?: boolean;
  setValue?: UseFormSetValue<ValuesType>;
  registerForm?: boolean;
  isDisabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
}

const SingleRadio = ({
  item,
  register,
  formKey,
  required,
  identifierForOther,
  setValue,
  registerForm,
  isDisabled,
  isIndicationInputEnabled,
}: singleRadioProps) => {
  const showOtherInput = identifierForOther && item === 'Other';
  const defaultLocationKey = useAppSelector(formsSelectors.defaultLocationKeySelector);
  const dispatch = useAppDispatch();

  const defaultLocation = () => {
    if (formKey.includes('provider:location'))
      if (setValue && defaultLocationKey !== formKey) {
        setValue(defaultLocationKey, false);
        dispatch(formsActions.setDefaultLocationKey(formKey));
      } else {
        dispatch(formsActions.setDefaultLocationKey(formKey));
      }
  };

  return (
    <div className="single-radio-wrapper d-sm-flex pb-2 pb-sm-0">
      <div className="radio d-flex">
        <input
          className="radio-button-input form-check-input radio-btn cursor-pointer"
          type="radio"
          value={item}
          id={registerForm ? formKey : `${item.replaceAll(' ', '-').toLowerCase()}-radio-input`}
          disabled={isDisabled}
          {...register(formKey, {
            onChange: registerForm ? defaultLocation : undefined,
            required: {
              value: required ? true : false,
              message: FIELD_MISSING,
            },
          })}
        />
        <label
          className="radio-button-label form-check-label cursor-pointer justify-content-center"
          htmlFor={
            registerForm ? formKey : `${item.replaceAll(' ', '-').toLowerCase()}-radio-input`
          }
        >
          {item}
        </label>
      </div>
      {showOtherInput && (
        <div className="ms-sm-2 radio-other-input">
          <FormField
            type="text"
            identifier={identifierForOther}
            isDisabled={!isIndicationInputEnabled}
            placeholder="Enter Indication for Use"
            validation={{
              minLength: {
                value: 5,
                message: VALUE_TOO_SHORT,
              },
            }}
          />
        </div>
      )}
    </div>
  );
};

const RadioButton = ({
  form,
  required,
  register,
  formKey,
  errors,
  identifierForOther,
  isIndicationInputEnabled,
  setValue,
  registerForm,
  isDisabled,
  orientation,
}: radioButtonProps) => {
  const { options, title, id } = form;

  const validationBudge = errors && errors[formKey] && (
    <ErrorBadge
      errors={errors}
      formKey={formKey}
    />
  );

  return (
    <>
      <div>{!registerForm && title(required)}</div>
      <span
        className={`d-flex ${orientation === 'horizontal' ? 'flex-row' : 'flex-column'} flex-wrap`}
        data-testid="radiobutton"
      >
        {options.map((item, index) => {
          return (
            <SingleRadio
              setValue={setValue}
              required={required}
              formKey={formKey}
              register={register}
              key={id + index}
              item={item}
              identifierForOther={identifierForOther}
              isIndicationInputEnabled={isIndicationInputEnabled}
              registerForm={registerForm}
              isDisabled={isDisabled}
            />
          );
        })}
      </span>
      {validationBudge}
    </>
  );
};

export default RadioButton;
